import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { subscribeWithSelector } from "zustand/middleware";
import { canPlayType } from "@/lib/utils";
import type { PlaybackStatus } from "../ports";
import type { AudioModel, BaseAudioModel } from "@coffitivity/shared";

type PlaybackState = {
	currentTrack: AudioModel | BaseAudioModel | null;
	previousTrack: AudioModel | BaseAudioModel | null;
	isCurrentTrackAnAd: boolean;
	isUnlocked: boolean;
	masterVolume: number;
	previousMasterVolume: number;
	preferredMimeType: "mp3" | "ogg";
	status: PlaybackStatus;
};

type PlaybackActions = {
	clearCurrentTrack: () => void;
	moveCurrentTrackToPrevious: () => void;
	movePreviousTrackToCurrent: () => void;
	setIsUnlocked: (isUnlocked: boolean) => void;
	setPreferredMimeType: (preferredMimeType: "mp3" | "ogg") => void;
	setCurrentTrack: (track: AudioModel | BaseAudioModel) => void;
	setCurrentTrackStatus: (status: PlaybackStatus) => void;
	setVolume: (volume: number, trackId?: string) => void;
	toggleMute: () => void;
};

export type PlaybackStoreProps = PlaybackState & PlaybackActions;

const isAdTrack = (track: AudioModel | BaseAudioModel) => track?.type === "ad";

export const playbackStore = createStore<PlaybackStoreProps>()(
	subscribeWithSelector(
		immer((set) => ({
			currentTrack: null,
			previousTrack: null,
			isCurrentTrackAnAd: false,
			isUnlocked: false,
			masterVolume: 1,
			previousMasterVolume: 1,
			preferredMimeType: canPlayType("audio/ogg") ? "ogg" : "mp3",
			status: "UNINITIALIZED",
			clearCurrentTrack: () => set({ currentTrack: null }),
			moveCurrentTrackToPrevious: () =>
				set((state) => {
					if (state.currentTrack) {
						state.previousTrack = state.currentTrack;
						state.currentTrack = null;
					}
				}),
			movePreviousTrackToCurrent: () =>
				set((state) => {
					if (state.previousTrack) {
						state.currentTrack = state.previousTrack;
						state.previousTrack = null;
					}
				}),
			setIsUnlocked: (isUnlocked) => set({ isUnlocked }),
			setPreferredMimeType: (preferredMimeType) => set({ preferredMimeType }),
			setCurrentTrack: (track) => {
				set({ currentTrack: track });
				set({ isCurrentTrackAnAd: isAdTrack(track) });
			},
			setCurrentTrackStatus: (status: PlaybackStatus) =>
				set((state) => {
					state.status = status;
				}),
			setVolume: (volume) =>
				set((state) => {
					// set the last master volume value before changing it
					state.previousMasterVolume = state.masterVolume;
					// Update the master volume
					state.masterVolume = volume;
				}),
			toggleMute: () =>
				set((state) => {
					if (state.masterVolume > 0) {
						state.previousMasterVolume = state.masterVolume;
						state.masterVolume = 0;
					} else {
						state.masterVolume = state.previousMasterVolume;
					}
				}),
		})),
	),
);

export type PlaybackStore = typeof playbackStore;

// hook to use the store in react components
export const usePlaybackStore = () => {
	const store = useStore(playbackStore);
	return store;
};
